// Pagina delle agende

import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Alert,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    IconButton,
    Tooltip,
    TextField,
    InputAdornment,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import {
    CalendarToday as CalendarIcon,
    Refresh as RefreshIcon,
    Search as SearchIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    MedicalServices as MedicalServicesIcon,
    Schedule as ScheduleIcon,
    Person as PersonIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    EventBusy as SuspensionIcon
} from '@mui/icons-material';
import { useAppContext } from '../context/AppContext';
import DiaryForm from '../components/diaries/DiaryForm';
import SuspensionsDialog from '../components/SuspensionsDialog';
import DiarySchemasDialog from '../components/DiarySchemasDialog';
import DiaryOrdersDialog from '../components/DiaryOrdersDialog';
import DeleteConfirmationDialog from '../components/DeleteConfirmationDialog';
import { Diary } from '../types';
import api from '../services/api';
import { 
    deleteDiary,
    DeleteOptions,
    DeleteResult 
} from '../services/diaryService';

const DiariesPage: React.FC = () => {
    const { state, dispatch } = useAppContext();
    const [showForm, setShowForm] = useState(false);
    const [editingDiary, setEditingDiary] = useState<Diary | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [executionUnitFilter, setExecutionUnitFilter] = useState('all');
    
    // Suspensions Dialog
    const [showSuspensionsDialog, setShowSuspensionsDialog] = useState(false);
    const [selectedDiaryForSuspensions, setSelectedDiaryForSuspensions] = useState<Diary | null>(null);
    
    // Diary Schemas Dialog
    const [showSchemasDialog, setSchemasDialog] = useState(false);
    const [selectedDiaryForSchemas, setSelectedDiaryForSchemas] = useState<Diary | null>(null);
    
    // Diary Orders Dialog
    const [showOrdersDialog, setShowOrdersDialog] = useState(false);
    const [selectedDiaryForOrders, setSelectedDiaryForOrders] = useState<Diary | null>(null);
    
    // Delete Dialog
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [selectedDiaryForDelete, setSelectedDiaryForDelete] = useState<Diary | null>(null);
    
    // Check if user is admin (simplified check - adjust based on your auth system)
    const userIsAdmin = state.user?.role === 'admin';
    
    // Carica le agende all'avvio
    useEffect(() => {
        loadDiaries();
    }, []);
    
    const loadDiaries = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await api.get('/recup-sync/diaries');
            if (response.data.success) {
                dispatch({ 
                    type: 'SET_DIARIES', 
                    payload: response.data.data || []
                });
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Errore nel caricamento delle agende');
            console.error('Error loading diaries:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (diary: Diary) => {
        setEditingDiary(diary);
        setShowForm(true);
    };
    
    const handleOpenSuspensions = (diary: Diary) => {
        setSelectedDiaryForSuspensions(diary);
        setShowSuspensionsDialog(true);
    };
    
    const handleCloseSuspensions = () => {
        setShowSuspensionsDialog(false);
        setSelectedDiaryForSuspensions(null);
    };
    
    const handleOpenSchemas = (diary: Diary) => {
        setSelectedDiaryForSchemas(diary);
        setSchemasDialog(true);
    };
    
    const handleCloseSchemas = () => {
        setSchemasDialog(false);
        setSelectedDiaryForSchemas(null);
    };
    
    const handleOpenOrders = (diary: Diary) => {
        setSelectedDiaryForOrders(diary);
        setShowOrdersDialog(true);
    };
    
    const handleCloseOrders = () => {
        setShowOrdersDialog(false);
        setSelectedDiaryForOrders(null);
    };

    const handleDeleteClick = (diary: Diary) => {
        setSelectedDiaryForDelete(diary);
        setShowDeleteDialog(true);
    };

    const handleDeleteConfirm = async (options: DeleteOptions) => {
        if (!selectedDiaryForDelete) return;

        setLoading(true);
        setError(null);
        
        try {
            const result: DeleteResult = await deleteDiary(selectedDiaryForDelete.id, options);
            
            // Rimuovi l'agenda dalla lista locale
            dispatch({ 
                type: 'DELETE_DIARY', 
                payload: selectedDiaryForDelete.id 
            });
            
            // Messaggio di successo dettagliato
            if (options.isHardDelete && result.details) {
                const { local_deleted, recup_deleted, recup_id } = result.details;
                let successMessage = result.message;
                
                if (recup_id) {
                    successMessage += local_deleted && recup_deleted
                        ? ' (eliminata da database locale e RECUP)'
                        : local_deleted && !recup_deleted
                        ? ' (eliminata da database locale, RECUP sync da implementare)'
                        : ' (stato parziale)';
                }
                
                // Show success as alert since there's no success state in this component
                alert(successMessage);
            } else {
                alert(result.message || 'Agenda eliminata con successo');
            }
            
            setShowDeleteDialog(false);
            setSelectedDiaryForDelete(null);
            
            // Ricarica i dati per essere sicuri
            await loadDiaries();
        } catch (err: any) {
            console.error('Errore nell\'eliminazione dell\'agenda:', err);
            
            let errorMessage = 'Errore nell\'eliminazione dell\'agenda';
            if (err.response?.data?.error) {
                errorMessage = err.response.data.error;
            } else if (err.message) {
                errorMessage = err.message;
            }
            
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (diary: Diary) => {
        setLoading(true);
        setError(null);
        try {
            const isUpdate = !!diary.id;
            
            console.log('🔍 DiariesPage handleSave:', {
                diary_id: diary.id,
                diary_id_type: typeof diary.id,
                diary_id_toString: String(diary.id),
                isUpdate: isUpdate,
                method: isUpdate ? 'PUT' : 'POST',
                endpoint: isUpdate ? `/recup-sync/diaries/${diary.id}` : '/recup-sync/diaries'
            });
            
            const response = isUpdate
                ? await api.put(`/recup-sync/diaries/${diary.id}`, diary)
                : await api.post('/recup-sync/diaries', diary);
            
            if (response.data.success) {
                if (isUpdate) {
                    dispatch({ 
                        type: 'UPDATE_DIARY', 
                        payload: response.data.data || diary 
                    });
                } else {
                    dispatch({ 
                        type: 'ADD_DIARY', 
                        payload: response.data.data || diary 
                    });
                }
                
                setShowForm(false);
                setEditingDiary(null);
                
                // Ricarica i dati per essere sicuri
                await loadDiaries();
            } else {
                throw new Error(response.data.message || `Errore nel ${isUpdate ? 'aggiornamento' : 'salvataggio'} dell'agenda`);
            }
        } catch (err: any) {
            setError(err.response?.data?.message || err.message || 'Errore nel salvataggio dell\'agenda');
            console.error('Errore nel salvataggio:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setShowForm(false);
        setEditingDiary(null);
    };

    // Filtri le agende in base ai criteri di ricerca
    const filteredDiaries = state.diaries.filter(diary => {
        const matchesSearch = searchTerm === '' || 
            diary.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            diary.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            diary.description?.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesStatus = statusFilter === 'all' || 
            diary.status_name?.toLowerCase() === statusFilter.toLowerCase();
        
        const matchesExecutionUnit = executionUnitFilter === 'all' || 
            diary.execution_unit_id === executionUnitFilter;
        
        return matchesSearch && matchesStatus && matchesExecutionUnit;
    });

    // Ottieni unità eroganti uniche per il filtro
    const uniqueExecutionUnits = Array.from(
        new Set(state.diaries.map(diary => diary.execution_unit_id).filter(Boolean))
    );

    return (
        <Box sx={{ p: 3 }}>
            <Card>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Typography variant="h5" component="h2">
                            <CalendarIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                            Gestione Agende
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Button
                                variant="outlined"
                                startIcon={<RefreshIcon />}
                                onClick={loadDiaries}
                                disabled={loading}
                            >
                                Ricarica
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<CalendarIcon />}
                                onClick={() => setShowForm(true)}
                            >
                                Aggiungi Agenda
                            </Button>
                        </Box>
                    </Box>

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}

                    {/* Filtri */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                placeholder="Cerca per nome, codice o descrizione..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth>
                                <InputLabel>Stato</InputLabel>
                                <Select
                                    value={statusFilter}
                                    label="Stato"
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                >
                                    <MenuItem value="all">Tutti</MenuItem>
                                    <MenuItem value="active">Attivo</MenuItem>
                                    <MenuItem value="inactive">Inattivo</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth>
                                <InputLabel>Unità Erogante</InputLabel>
                                <Select
                                    value={executionUnitFilter}
                                    label="Unità Erogante"
                                    onChange={(e) => setExecutionUnitFilter(e.target.value)}
                                >
                                    <MenuItem value="all">Tutte</MenuItem>
                                    {uniqueExecutionUnits.map(unitId => (
                                        <MenuItem key={unitId} value={unitId}>
                                            {unitId}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>

                    {/* Contenuto principale */}
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress />
                            <Typography sx={{ ml: 2 }}>Caricamento agende...</Typography>
                        </Box>
                    ) : showForm ? (
                        <DiaryForm
                            diary={editingDiary}
                            onSave={handleSave}
                            onCancel={handleCancel}
                        />
                    ) : (
                        <>
                            {/* Statistiche */}
                            <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <Chip
                                    icon={<CalendarIcon />}
                                    label={`${filteredDiaries.length} agende visualizzate`}
                                    color="primary"
                                    variant="outlined"
                                />
                                <Chip
                                    icon={<CheckCircleIcon />}
                                    label={`${filteredDiaries.filter(d => d.online).length} online`}
                                    color="success"
                                    variant="outlined"
                                />
                                <Chip
                                    icon={<MedicalServicesIcon />}
                                    label={`${uniqueExecutionUnits.length} unità eroganti`}
                                    color="info"
                                    variant="outlined"
                                />
                            </Box>

                            {/* Tabella agende */}
                            <TableContainer component={Paper} variant="outlined">
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <CalendarIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                                                    Nome Agenda
                                                </Box>
                                            </TableCell>
                                            <TableCell>Codice</TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <MedicalServicesIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                                                    Unità Erogante
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <PersonIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                                                    Medico
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <ScheduleIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                                                    Durata Slot
                                                </Box>
                                            </TableCell>
                                            <TableCell>Stato</TableCell>
                                            <TableCell>Sospensioni</TableCell>
                                            <TableCell>Fasce Orarie</TableCell>
                                            <TableCell>Ordini/Prestazioni</TableCell>
                                            <TableCell>Ultimo Aggiornamento</TableCell>
                                            <TableCell align="center">Azioni</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredDiaries.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                                                    <Typography variant="body1" color="textSecondary">
                                                        {searchTerm || statusFilter !== 'all' || executionUnitFilter !== 'all' 
                                                            ? 'Nessuna agenda trovata con i filtri applicati'
                                                            : 'Nessuna agenda disponibile'
                                                        }
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            filteredDiaries.map((diary) => (
                                                <TableRow key={diary.id} hover>
                                                    <TableCell>
                                                        <Box>
                                                            <Typography variant="subtitle2" fontWeight="bold">
                                                                {diary.name || diary.description}
                                                            </Typography>
                                                            {diary.description && diary.name !== diary.description && (
                                                                <Typography variant="body2" color="textSecondary">
                                                                    {diary.description}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" fontFamily="monospace">
                                                            {diary.code}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        {diary.execution_unit_id && (
                                                            <Chip
                                                                size="small"
                                                                label={diary.execution_unit_id}
                                                                color="primary"
                                                                variant="outlined"
                                                            />
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2">
                                                            {diary.default_unit_doctor_name || '-'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                            <ScheduleIcon sx={{ mr: 0.5, fontSize: '1rem', color: 'action.active' }} />
                                                            <Typography variant="body2">
                                                                {diary.slot_size || diary.def_order_duration_mins || '-'} min
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            size="small"
                                                            icon={diary.online ? <CheckCircleIcon /> : <ErrorIcon />}
                                                            label={diary.online ? 'Online' : 'Offline'}
                                                            color={diary.online ? 'success' : 'error'}
                                                            variant="outlined"
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Tooltip title="Gestisci sospensioni agenda">
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                startIcon={<SuspensionIcon />}
                                                                onClick={() => handleOpenSuspensions(diary)}
                                                                sx={{ minWidth: 'auto' }}
                                                            >
                                                                Sospensioni
                                                            </Button>
                                                        </Tooltip>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Tooltip title="Gestisci fasce orarie agenda">
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                startIcon={<ScheduleIcon />}
                                                                onClick={() => handleOpenSchemas(diary)}
                                                                sx={{ minWidth: 'auto' }}
                                                                color="secondary"
                                                            >
                                                                Fasce
                                                            </Button>
                                                        </Tooltip>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Tooltip title="Gestisci ordini/prestazioni agenda">
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                startIcon={<MedicalServicesIcon />}
                                                                onClick={() => handleOpenOrders(diary)}
                                                                sx={{ minWidth: 'auto' }}
                                                                color="info"
                                                            >
                                                                Ordini
                                                            </Button>
                                                        </Tooltip>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" color="textSecondary">
                                                            {diary.last_update 
                                                                ? new Date(diary.last_update).toLocaleDateString()
                                                                : '-'
                                                            }
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                                            <Tooltip title="Modifica agenda">
                                                                <IconButton
                                                                    size="small"
                                                                    color="primary"
                                                                    onClick={() => handleEdit(diary)}
                                                                >
                                                                    <EditIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Elimina agenda">
                                                                <IconButton
                                                                    size="small"
                                                                    color="error"
                                                                    onClick={() => handleDeleteClick(diary)}
                                                                >
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    )}
                </CardContent>
            </Card>

            {/* Dialog Sospensioni */}
            {selectedDiaryForSuspensions && (
                <SuspensionsDialog
                    open={showSuspensionsDialog}
                    onClose={handleCloseSuspensions}
                    diaryId={selectedDiaryForSuspensions.id}
                    diaryName={selectedDiaryForSuspensions.name || selectedDiaryForSuspensions.description || 'Agenda'}
                />
            )}
            
            {/* Dialog Schemi Agenda */}
            {selectedDiaryForSchemas && (
                <DiarySchemasDialog
                    open={showSchemasDialog}
                    onClose={handleCloseSchemas}
                    diaryId={selectedDiaryForSchemas.id}
                    diaryName={selectedDiaryForSchemas.name || selectedDiaryForSchemas.description || 'Agenda'}
                />
            )}
            
            {/* Dialog Ordini Agenda */}
            {selectedDiaryForOrders && (
                <DiaryOrdersDialog
                    open={showOrdersDialog}
                    onClose={handleCloseOrders}
                    diaryId={selectedDiaryForOrders.id}
                    diaryName={selectedDiaryForOrders.name || selectedDiaryForOrders.description || 'Agenda'}
                />
            )}
            
            {/* Dialog di conferma eliminazione */}
            <DeleteConfirmationDialog
                open={showDeleteDialog}
                onClose={() => setShowDeleteDialog(false)}
                onConfirm={handleDeleteConfirm}
                title="Elimina Agenda"
                itemName={selectedDiaryForDelete?.name || selectedDiaryForDelete?.description || ''}
                entityType="agenda"
                allowHardDelete={true}
                userIsAdmin={userIsAdmin}
                relatedData={{
                    executionUnit: selectedDiaryForDelete?.execution_unit_name,
                    status: selectedDiaryForDelete?.status_name
                }}
            />
        </Box>
    );
};

export default DiariesPage;
